import React, { useEffect, useState, useRef } from 'react'
import { setCurrentPageTitle } from '../../store/slices/pageSlice';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useSnackbar } from 'notistack';
import { createQuotationTemplate, deleteQuotationTemplate, fetchAllQuotationTemplates, updateQuotationTemplate } from '../../store/slices/QuotationTemplateSlice';
import { fetchCompanyProfiles } from '../../store/slices/companyProfileSlice';
import AdminLoader from '../../components/common/loader/AdminLoader';
import { SquarePen, Trash2, EllipsisVertical } from "lucide-react";
import { getFeatureAccessLevel } from '../../utils/featureAccessUtils';

const TEMPLATE_OPTIONS = [
  'Project Name',
  'Client Name',
  'Current Date',
  'Client Contact Name',
  'Client Contact Email',
  'Company Profile Name',
  'Company Profile Logo',
  'Company Profile Email',
  'Company Profile Address',
  'Summary Tables'
];

const CRM_EXTRA_TEMPLATE_FIELDS = [
  'Structural Monitoring Proposal',
  'Project Background',
];

const QUOTATION_TEMPLATE_DEFAULT_ERP = `
<p>
  <p>Hello {{Client Name}}</p>
  <p>{{Company Profile Name}}</p>
  <p>
    We are pleased to share the quotation for <strong>{{Project Name}}</strong>. 
    This quotation has been prepared based on the discussed scope of work, 
    required resources, applicable effort estimates, and associated costs 
    including manpower, hardware, and other applicable expenses.
  </p>
  <p>
    Kindly review the quotation details and let us know if any clarification, 
    modification, or adjustment is required. Upon your approval, we will 
    proceed with the next steps as per the agreed terms.
  </p>
  <p>Thank you</p>
  <p>{{Current Date}}</p>
</p>
`;

const QuotationTemplate = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState(QUOTATION_TEMPLATE_DEFAULT_ERP);
  const quillRef = useRef(null);
  const [isLoading, SetisLoading] = useState(false);
  const { templates } = useSelector((state) => state.quotationTemplates);
  const { profiles } = useSelector((state) => state.companyProfile);
  const subscriptionPackage = String(profiles?.[0]?.subscriptionpackage || '').trim().toUpperCase();
  const shouldShowExtraTemplateFields = ['CRM', 'ERP'].includes(subscriptionPackage);
  const { employees } = useSelector((state) => state.employees);

  const emailForAccess = typeof localStorage !== "undefined" ? localStorage.getItem("email") : "";
  const currentEmployee = employees?.find((emp) => emp.EmpEmail === emailForAccess) ?? null;
  const hasNoFeatureConfig = !currentEmployee || !Array.isArray(currentEmployee?.featureAccess) || currentEmployee.featureAccess.length === 0;
  const qtCreateLevel = getFeatureAccessLevel(currentEmployee, "QUOTATION TEMPLATE CREATE QUOTATION TEMPLATE");
  const qtEditLevel = getFeatureAccessLevel(currentEmployee, "QUOTATION TEMPLATE EDIT QUOTATION TEMPLATE");
  const isViewOnly = !!currentEmployee && !hasNoFeatureConfig && (
    (qtCreateLevel && String(qtCreateLevel).trim().toUpperCase() === "VIEW ONLY") ||
    (qtEditLevel && String(qtEditLevel).trim().toUpperCase() === "VIEW ONLY")
  );
  const [templateDateid, settemplateDateid] = useState(null);
  const [TemplateName, setTemplateName] = useState('');

  const [cursorPosition, setCursorPosition] = useState(null);

  const [hasUserEdited, setHasUserEdited] = useState(false);

  // Salesperson name for the signature line (CRM templates only).
  const salesPersonName = `${localStorage.getItem('name') || ''} ${localStorage.getItem('lastName') || ''}`.trim();

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    quill.on('selection-change', (range) => {
      if (range) {
        setCursorPosition(range.index);
      }
    });
  }, []);

  const dropdownsRef = useRef(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If dropdown is open and click is outside the dropdown AND not on the dropdown icon
      if (openDropdownId && dropdownsRef.current && !dropdownsRef.current.contains(event.target)) {
        // Also check if the click was on the ellipsis icon itself
        const clickedElement = event.target;
        const isEllipsisIcon = clickedElement.closest('.dropdown-toggle-icon');

        if (!isEllipsisIcon) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);



  useEffect(() => {
    dispatch(fetchAllQuotationTemplates());
    dispatch(fetchCompanyProfiles());

  }, [dispatch]);

  useEffect(() => {
    const pkg = String(profiles?.[0]?.subscriptionpackage || '').trim().toUpperCase();
    if (pkg !== 'CRM') return;
    if (templateDateid) return; // Only for "Create" mode
    if (hasUserEdited) return;
    // ReactQuill can normalize whitespace in HTML; use a content-based check instead of strict equality.
    const looksLikeDefaultTemplate =
      value.includes('Hello {{Client Name}}') &&
      value.includes('{{Company Profile Name}}') &&
      value.includes('{{Current Date}}');
    if (!looksLikeDefaultTemplate) return;

    const CRM_DEFAULT_TEMPLATE = `
<p>To, "{{Client Name}}"</p>
<p>Dear "{{Client Name}}" ,</p>
<p>Date: {{Current Date}}</p>
<p>
  We are pleased to present this proposal for Structural Monitoring system for your
  upcoming bridge infrastructure project. Our solution integrates advanced sensing
  technology and visual surveillance tools to ensure timely reporting, and efficient
  maintenance planning.
</p>
<p>
  We are confident that our system will enhance structural safety, reduce inspection costs,
  and contribute to the long-term resilience of the bridge structure.
</p>
<p>Thank you for the opportunity to collaborate.</p>
<p>Sincerely,</p>
<p>${salesPersonName}</p>
<p>{{Company Profile Name}}</p>
`;

    setValue(CRM_DEFAULT_TEMPLATE);
  }, [profiles, templateDateid, hasUserEdited, value, salesPersonName]);

  const loadTemplate = async (tplUrl, enqueueSnackbar) => {

    try {
      const res = await fetch(tplUrl);
      if (!res.ok) throw new Error("Failed to fetch template");

      const html = await res.text();

      // Optional: parse to ensure it's valid HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.body.innerHTML;

      setValue(content); // Pass this HTML string to ReactQuill
    } catch (error) {
      console.error("Error loading template:", error);
      if (enqueueSnackbar) {
        enqueueSnackbar("Failed to load template", { variant: "error" });
      }
    }
  };



  useEffect(() => {
    dispatch(setCurrentPageTitle("Quotation Template"));
  }, [dispatch]);

  const handleOptionClick = (option) => {
    if (isViewOnly) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    let placeholderHTML = '';

    if (option === 'Company Profile Logo') {
      placeholderHTML = `<img src="{{Company Profile Logo}}" alt="Company Logo" style="width: 100px;" />`;
    } else if (CRM_EXTRA_TEMPLATE_FIELDS.includes(option)) {
      // Insert only the token. The replacement engine will inject the full multi-paragraph HTML later.
      placeholderHTML = `{{${option}}}`;
    } else {
      placeholderHTML = `<span style="font-weight: bold">{{${option}}}</span>`;
    }

    const insertIndex = cursorPosition ?? quill.getLength(); // use saved position or fallback
    quill.clipboard.dangerouslyPasteHTML(insertIndex, placeholderHTML);

    // move cursor right after inserted content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = placeholderHTML;
    const plainTextLength = tempDiv.innerText.length;
    const nextIndex = insertIndex + plainTextLength;
    quill.setSelection(nextIndex, 0);
    quill.focus();
  };


  const handleQuotation = async () => {
    if (isViewOnly) return;

    const formData = new FormData();
    formData.append('templatedata', value); // Make sure this matches server expectation
    formData.append('templatename', TemplateName); // Make sure this matches server expectation
    if (!TemplateName) {
      enqueueSnackbar("Please add template name", {
        variant: "error",
      });
      return
    }

    SetisLoading(true);
    try {
      if (templateDateid) {
        await dispatch(updateQuotationTemplate({ id: templateDateid, updatedData: formData })).unwrap();
        enqueueSnackbar("Quotation Template Successfully Updated", {
          variant: "success",
        });
      } else {
        await dispatch(createQuotationTemplate(formData)).unwrap();
        enqueueSnackbar("Quotation Template Successfully Created", {
          variant: "success",
        });
      }

    } catch (error) {
      console.error("Failed to create quotation template:", error);
      const errorMessage =
        error?.error ||
        error?.message ||
        "Something went wrong. Please try again.";
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    } finally {
      SetisLoading(false);
      setValue("")
      settemplateDateid(null)
      fetchAllQuotationTemplates()
      setTemplateName("")
      dispatch(fetchAllQuotationTemplates());

    }
  };



  const handleTemplateDelete = async (id) => {
    if (isViewOnly) return;
    SetisLoading(true);
    try {
      await dispatch(deleteQuotationTemplate(id)).unwrap();
      enqueueSnackbar("Quotation Template Deleted Successfully Created", {
        variant: "success",
      });
    } catch (error) {
      console.error("Failed to create quotation template:", error);
      const errorMessage =
        error?.error ||
        error?.message ||
        "Something went wrong. Please try again.";
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    } finally {
      SetisLoading(false);
    }
  };

    const PREDEFINED_TEMPLATE_1 = `
  <p>
  <p> Hello {{Client Name}}</p>
  <p>{{Company Profile Name}}</p>
  <p>I am Kaushal Working on {{Project Name}}, I would like to request the necessary hardware required to carry out my responsibilities effectively. Kindly provide details on the process for obtaining the required equipment, or let me know if I should submit a formal requisition.</p>
  <p>Thank you</p>
  <p>{{Current Date}}</p>

  </p>

  `;

  


  return (
    <div className='pb-5'>
      {isLoading ? <AdminLoader isLoading={isLoading} /> : <></>}
      <div className='pt-2'>
        <div className='flex min-h-[30rem] rounded-md  gap-2'>
          <div className='w-[70%] px-3 py-2 flex flex-col bg-white border rounded-md'>
            <div className="w-full flex justify-between gap-1 items-center p-1">
              <h1 className="text-xl mb-2 font-semibold">
                {templateDateid ? "Update Template" : "Create Template"}
              </h1>
            </div>
            <div className="w-full flex justify-start gap-1 items-center p-1">
              <div className='inline-block'>
                <input
                  disabled={isViewOnly}
                  className={`border w-full rounded-md p-2 overflow-hidden outline-none ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                  type="text"
                  placeholder="Enter Template Name"
                  value={TemplateName}
                  onChange={(e) => {
                    if (isViewOnly) return;
                    setTemplateName(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className='flex-grow max-h-[20rem] overflow-y-auto'>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value}
                readOnly={isViewOnly}
                onChange={(next) => {
                  if (isViewOnly) return;
                  setHasUserEdited(true);
                  setValue(next);
                }}
                className={`h-[80%] p-2 outline-none rounded-md resize-none ${isViewOnly ? "opacity-60 pointer-events-none" : ""}`}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                    [{ 'font': [] }],
                    [{ 'align': [] }],
                    ['image'],
                  ]
                }}
                formats={[
                  'header', 'font', 'size',
                  'bold', 'italic', 'underline', 'strike', 'blockquote',
                  'list', 'bullet', 'indent',
                  'link', 'image', 'video'
                ]}

              />
            </div>
            <div className='flex justify-end items-center pt-2'>
              <button
                disabled={isViewOnly}
                onClick={handleQuotation}
                className={`p-2 rounded-md font-semibold ${isViewOnly ? "bg-[#d1d5db] text-[#9ca3af] cursor-not-allowed pointer-events-none" : "bg-[#1d4ed8] text-white"}`}
              >
                {templateDateid ? "Update" : "Create"}
              </button>
            </div>

            <div className='pl-2'>
              <h3 className="text-md mb-2 font-semibold">Predefine Template</h3>
              <div className='p-2 w-full'>
                <div onClick={() => { if (isViewOnly) return; setValue(PREDEFINED_TEMPLATE_1); }}
                  className={`rounded-md shadow-lg inline-block p-3 border ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "cursor-pointer"}`}>
                  Template 1
                </div>
              </div>
            </div>
          </div>

          <div className='optionbox w-[30%] p-3  border rounded-md bg-white'>
            <div className='p-2 border-b border-[#4d908d] mb-2'>
              <h2 className='text-xl font-semibold'>Fields</h2>
            </div>

            <div className=' max-h-[23rem] overflow-auto pb-5'>
              {(shouldShowExtraTemplateFields ? [...TEMPLATE_OPTIONS, ...CRM_EXTRA_TEMPLATE_FIELDS] : TEMPLATE_OPTIONS).map((option, index) => (
                <div
                  key={option}
                  className={`w-full p-2 mb-1 border rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "cursor-pointer hover:bg-gray-200"}`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option}
                </div>
              ))}

            </div>

          </div>
        </div>
      </div>


      <div className='bg-white w-full rounded-md py-5 mt-2'>
        <div className='px-3'>
          <h1 className="text-2xl mb-2 font-semibold">Saved Template</h1>
        </div>
        <hr />
        <div className={`list w-full max-h-[21rem] ${openDropdownId ? 'overflow-visible' : 'overflow-auto'}`}>
          <div className='w-full flex sticky top-0 bg-[#e8e8e8]'>
            <div className='w-[20%] p-2 flex justify-center align-middle items-center font-semibold'>
              Sr. No
            </div>
            <div className='w-[40%] p-2 flex justify-center align-middle items-center font-semibold'>
              Template Name
            </div>
            <div className='w-[40%] p-2 flex justify-center align-middle items-center font-semibold'>
              Action
            </div>
            {/* <div className='w-[20%] p-2 flex justify-center align-middle items-center font-semibold'>
              <FaTrash className="w-4 h-4" />
            </div> */}
          </div>
          {templates?.map((template, index) => (
            <div key={template._id || index} className='w-full flex'>
              <div className='w-[20%] border-b p-2 flex justify-center align-middle items-center font-semibold'>
                {index + 1}
              </div>
              <div className='w-[40%] border-b p-2 flex justify-center align-middle items-center font-semibold'>
                {template.templatename}
              </div>
              <div className='w-[40%] border-b p-2 flex justify-center align-middle items-center font-semibold'>
                <div className="px-2 relative">
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isViewOnly) return;
                      if (openDropdownId !== template._id) {
                        const buttonRect = e.currentTarget.getBoundingClientRect();
                        setDropdownPosition({
                          top: buttonRect.bottom + 4,
                          left: buttonRect.left - 12
                        });
                        setOpenDropdownId(template._id);
                      } else {
                        setOpenDropdownId(null);
                      }
                    }}
                    className={`p-1 dropdown-toggle-icon ${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "cursor-pointer"}`}
                  >
                    <EllipsisVertical />
                  </div>
                  {openDropdownId === template._id && (
                    <div
                      ref={dropdownsRef}
                      className="z-[100] p-1 absolute flex flex-col bottom-full mb-1 -left-3 bg-white rounded-md shadow-lg border min-w-[120px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        onClick={() => {
                          if (isViewOnly) return;
                          loadTemplate(template.templatedataUrl)
                          settemplateDateid(template._id)
                          setTemplateName(template.templatename)
                          setOpenDropdownId(null)
                        }}
                        className={`p-1 flex gap-1 w-full justify-start align-middle items-center border-b ${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "cursor-pointer hover:bg-gray-50"}`}
                      >
                        <span title="Edit">
                          <SquarePen size={15} className={isViewOnly ? "text-[#9ca3af]" : "text-blue-500"} />
                        </span>
                        Edit
                      </div>
                      <div
                        onClick={() => {
                          if (isViewOnly) return;
                          handleTemplateDelete(template._id)
                          setOpenDropdownId(null)
                        }}
                        className={`p-1 flex gap-1 w-full justify-start align-middle items-center ${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "cursor-pointer hover:bg-gray-50"}`}
                      >
                        <span title="Delete">
                          <Trash2 size={15} className={isViewOnly ? "text-[#9ca3af]" : "text-blue-500"} />
                        </span>
                        Delete
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* <div onClick={() => {
                loadTemplate(template.templatedataUrl)
                settemplateDateid(template._id)
                setTemplateName(template.templatename)
              }}
                className='w-[20%] border-b p-2 flex justify-center align-middle items-center font-semibold'>
                <MdEditSquare className="w-4 h-4 cursor-pointer" />
              </div>
              <div className='w-[20%] border-b p-2 flex justify-center align-middle items-center font-semibold'>
                <FaTrash onClick={() => handleTemplateDelete(template._id)} className="w-4 h-4 cursor-pointer" />
              </div> */}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default QuotationTemplate